import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { UUID_REGEX } from "@/lib/usernameUtils";
import { useQuery } from "@tanstack/react-query";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ListingCard } from "@/components/ListingCard";
import { useSeo } from "@/hooks/useSeo";
import { ProfileMiniMap } from "@/components/ProfileMiniMap";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { filterProvinces } from "@/lib/filterData";
import { resolveCoords } from "@/lib/municipalityCoords";
import { buildDirectThreadId } from "@/lib/threadIds";
import {
  MapPin, MessageSquare, Phone, Mail, Star, Loader2,
  Facebook, Instagram, Linkedin, MessageCircle, TreePine,
  Building2, Briefcase, Calendar, Users, Globe, Package,
  ShieldCheck, FileText,
} from "lucide-react";
import { useEffect, useState } from "react";

// Coordinates resolved via shared resolveCoords helper (covers all 290 kommuner)

function extractUsername(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/\/+$/, "");
    const segments = path.split("/").filter(Boolean);
    return segments[segments.length - 1] || url;
  } catch {
    return url;
  }
}

function getProvinceForLocation(location: string | null): { province: string | null; kommun: string | null } {
  if (!location) return { province: null, kommun: null };
  for (const p of filterProvinces) {
    if (p.municipalities.includes(location)) return { province: p.name, kommun: location };
  }
  const found = filterProvinces.find((p) => p.name === location);
  if (found) return { province: found.name, kommun: null };
  return { province: null, kommun: location };
}

function getCoordsForLocation(municipality: string | null, location: string | null): [number, number] {
  return resolveCoords({ municipality, location }) ?? [62.0, 15.0];
}

function getInitials(name?: string | null) {
  return name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) ?? "?";
}

function formatMemberSince(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
}

function StarRow({ rating, count }: { rating: number; count: number }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${i <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-foreground">{rating.toFixed(1).replace(".", ",")}</span>
      <span className="text-xs text-muted-foreground">
        ({count} {count === 1 ? "recension" : "recensioner"})
      </span>
    </div>
  );
}

export default function PublicProfile() {
  const { username: param } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("annonser");

  const requireAuth = (action: () => void) => {
    if (!user) { navigate("/logga-in"); return; }
    action();
  };

  // Public-safe columns (readable by both anon and authenticated users)
  const PUBLIC_PROFILE_COLS =
    "id, username, full_name, account_type, company_name, avatar_url, cover_image_url, location, province, municipality, country, business_form, industry, founded_year, employee_count, website, product_categories, facebook_url, instagram_url, linkedin_url, skogsforum_url, created_at, updated_at";

  const lookupKey = param ?? "";
  const lookupByUuid = !!lookupKey && UUID_REGEX.test(lookupKey);

  // Profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["public-profile", lookupKey, lookupByUuid, !!user],
    queryFn: async () => {
      const source = user ? "profiles" : "public_profiles";
      const cols = user ? "*" : PUBLIC_PROFILE_COLS;
      const filterCol = lookupByUuid ? "id" : "username";
      const { data, error } = await (supabase as any)
        .from(source).select(cols).eq(filterCol, lookupKey).maybeSingle();
      if (error) throw error;
      if (!data) return null;
      // Block direct URL access to admin profiles
      const { data: isAdmin } = await (supabase as any).rpc("is_admin", { _user_id: data.id });
      if (isAdmin === true) return null;
      return data as any;
    },
    enabled: !!lookupKey,
  });

  // Resolved user id used by all secondary queries
  const userId: string | undefined = profile?.id;

  // If accessed by UUID, redirect to clean username URL (after all hooks)
  const shouldRedirectToUsername = !profileLoading && lookupByUuid && profile?.username;
  useEffect(() => {
    if (shouldRedirectToUsername) {
      navigate(`/saljare/${profile!.username}`, { replace: true });
    }
  }, [shouldRedirectToUsername, profile, navigate]);

  const isCompany = profile?.account_type === "company";

  const seoName = profile
    ? (isCompany ? (profile.company_name || profile.full_name) : profile.full_name) || profile.username
    : null;
  useSeo({
    title: seoName ? `${seoName}${profile?.location ? ` — ${profile.location}` : ""}` : "Säljare",
    description: seoName
      ? `${seoName}${isCompany ? " erbjuder skogstjänster och produkter" : " säljer på TimberNet"}${profile?.location ? ` i ${profile.location}` : ""}. Se annonser och kontaktuppgifter.`.slice(0, 160)
      : "Profil på TimberNet — Sveriges marknadsplats för skog.",
    path: `/saljare/${profile?.username ?? param ?? ""}`,
    ogType: "profile",
  });
  const verified = isCompany && profile?.admin_approved === true;

  // Listings
  const { data: listings } = useQuery({
    queryKey: ["user-listings", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings").select("*").eq("seller_id", userId!).in("status", ["aktiv", "såld"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Reviews
  const { data: purchaseReviews } = useQuery({
    queryKey: ["purchase-reviews", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("purchase_reviews").select("*").eq("target_user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const authorIds = [...new Set(data.map((r: any) => r.author_id))];
      const { data: profiles } = await supabase
        .from("public_profiles").select("id, full_name, avatar_url").in("id", authorIds);
      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      return data.map((r: any) => ({ ...r, author_profile: profileMap.get(r.author_id) || null }));
    },
    enabled: !!userId,
  });

  // Services (only relevant for company)
  const { data: services } = useQuery({
    queryKey: ["user-services", userId],
    queryFn: async () => {
      const { data: pp } = await supabase
        .from("provider_profiles").select("id").eq("user_id", userId!);
      if (!pp || pp.length === 0) return [];
      const ids = pp.map((p: any) => p.id);
      const { data, error } = await supabase
        .from("services").select("*").in("provider_id", ids).eq("active", true);
      if (error) throw error;
      return data;
    },
    enabled: !!userId && isCompany,
  });

  // Contact persons (company only)
  const { data: contactPersons } = useQuery({
    queryKey: ["company-contact-persons", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_contact_persons").select("*")
        .eq("company_user_id", userId!)
        .order("position", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!userId && isCompany,
  });

  const reviewCount = purchaseReviews?.length ?? 0;
  const avgRating = reviewCount > 0
    ? purchaseReviews!.reduce((sum: number, r: any) => sum + r.rating, 0) / reviewCount
    : 0;

  const loc = getProvinceForLocation(profile?.location ?? null);
  const provinceKommun = loc.province && loc.kommun
    ? `${loc.province}, ${loc.kommun}`
    : loc.province ?? loc.kommun ?? null;
  const coords = getCoordsForLocation(profile?.municipality ?? loc.kommun, profile?.location ?? null);

  const displayName = isCompany
    ? (profile?.company_name || profile?.full_name)
    : profile?.full_name;
  const initials = getInitials(displayName);

  if (profileLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="flex flex-1 items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex-1 py-8 space-y-6">
          <div className="h-40 w-full rounded-lg bg-muted animate-pulse" />
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-5 w-1/3 rounded bg-muted animate-pulse" />
              <div className="h-3 w-1/4 rounded bg-muted animate-pulse" />
            </div>
          </div>
          <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="flex flex-1 items-center justify-center py-20">
          <p className="text-lg font-medium text-muted-foreground">Profilen hittades inte.</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Social badges
  const socialBadges: { icon: React.ReactNode; label: string | null; href: string }[] = [];
  if (profile.facebook_url) socialBadges.push({ icon: <Facebook className="h-4 w-4" />, label: extractUsername(profile.facebook_url), href: profile.facebook_url });
  if (profile.instagram_url) socialBadges.push({ icon: <Instagram className="h-4 w-4" />, label: extractUsername(profile.instagram_url), href: profile.instagram_url });
  if (profile.linkedin_url) socialBadges.push({ icon: <Linkedin className="h-4 w-4" />, label: extractUsername(profile.linkedin_url), href: profile.linkedin_url });
  if (profile.whatsapp_number) socialBadges.push({ icon: <MessageCircle className="h-4 w-4" />, label: profile.whatsapp_number, href: `https://wa.me/${profile.whatsapp_number.replace(/\D/g, "")}` });
  if (profile.skogsforum_url) socialBadges.push({ icon: <TreePine className="h-4 w-4" />, label: null, href: profile.skogsforum_url });

  const profileEmail = (isCompany ? (profile.business_email || profile.email) : profile.email) || null;
  const productCategories: string[] = profile.product_categories ?? [];
  const memberSince = formatMemberSince(profile.created_at);
  const hasBio = false; // profiles table currently has no bio column for personal accounts
  const hasSocials = socialBadges.length > 0;

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      {/* Banner */}
      {profile.cover_image_url ? (
        <div className="h-40 w-full md:h-52 overflow-hidden">
          <img src={profile.cover_image_url} alt={`Omslagsbild för ${displayName}`} className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="h-40 w-full bg-gradient-to-r from-primary/80 to-primary/40 md:h-52" />
      )}

      {/* Header */}
      <div className="container">
        <div className="-mt-14 md:-mt-16 mb-4">
          <Avatar className="h-24 w-24 border-4 border-background shadow-lg md:h-28 md:w-28">
            {profile.avatar_url ? <AvatarImage src={profile.avatar_url} alt={displayName} /> : null}
            <AvatarFallback className="bg-card text-primary text-2xl font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Left: name & meta */}
          <div className="space-y-1.5 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-xl font-bold md:text-2xl">{displayName}</h1>
              {verified && (
                <Badge className="bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15 gap-1">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verifierat företag
                </Badge>
              )}
            </div>
            {isCompany && ((Array.isArray(profile.industry) && profile.industry.length > 0) || profile.business_form) && (
              <p className="text-xs text-muted-foreground">
                {[
                  Array.isArray(profile.industry) ? profile.industry.join(", ") : profile.industry,
                  profile.business_form,
                ].filter(Boolean).join(" · ")}
              </p>
            )}
            {provinceKommun && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" /> {provinceKommun}
              </p>
            )}
            {memberSince && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" /> Medlem sedan {memberSince}
              </p>
            )}
            {reviewCount > 0 ? (
              <StarRow rating={avgRating} count={reviewCount} />
            ) : (
              <p className="text-xs text-muted-foreground">Inga recensioner ännu</p>
            )}
          </div>

          {/* Right: social cluster + CTA */}
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            {socialBadges.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {socialBadges.map((l, i) => (
                  <a
                    key={i}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-secondary text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground px-2.5 py-2"
                  >
                    {l.icon}
                    {l.label && <span className="max-w-[100px] truncate">{l.label}</span>}
                  </a>
                ))}
              </div>
            )}
            <Button
              className="shrink-0"
              onClick={() => requireAuth(() => navigate(`/meddelanden/${buildDirectThreadId(userId!, user!.id)}`))}
            >
              <MessageSquare className="mr-2 h-4 w-4" /> Skicka meddelande
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mt-3 flex-1 pb-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-transparent border-b border-border rounded-none p-0 h-auto overflow-x-auto">
            {[
              { val: "annonser", label: "Annonser" },
              { val: "om", label: "Om & Kontakt" },
              ...(isCompany ? [{ val: "tjanster", label: "Tjänster" }, { val: "personal", label: "Personal" }] : []),
            ].map(({ val, label }) => (
              <TabsTrigger
                key={val}
                value={val}
                className="rounded-none border-b-2 border-transparent px-4 py-2.5 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ===== Annonser ===== */}
          <TabsContent value="annonser" className="mt-6">
            {listings && listings.length > 0 ? (
              <>
                <p className="mb-3 text-sm text-muted-foreground">
                  {listings.length} {listings.length === 1 ? "aktiv annons" : "aktiva annonser"}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {listings.map((l: any) => (
                    <ListingCard
                      key={l.id}
                      listing={{
                        id: l.id,
                        sellerName: "",
                        category: l.category,
                        species: l.species,
                        title: l.title,
                        description: l.description ?? "",
                        price: Number(l.price),
                        priceUnit: l.price_unit ?? "",
                        location: l.location ?? "",
                        delivery: l.delivery,
                        images: l.images ?? [],
                        status: l.status,
                      }}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="py-12 text-center text-muted-foreground">
                Den här användaren har inga aktiva annonser just nu.
              </p>
            )}
          </TabsContent>

          {/* ===== Om & Kontakt ===== */}
          <TabsContent value="om" className="mt-6">
            <div className="grid gap-8 md:grid-cols-2">
              {/* Left column — facts + bio + reviews */}
              <div className="space-y-6">
                <div className="space-y-2.5">
                  {provinceKommun && (
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">{provinceKommun}</span>
                    </div>
                  )}
                  {memberSince && (
                    <div className="flex items-start gap-3 text-sm">
                      <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">Medlem sedan {memberSince}</span>
                    </div>
                  )}
                  {isCompany && profile.business_form && (
                    <div className="flex items-start gap-3 text-sm">
                      <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">{profile.business_form}</span>
                    </div>
                  )}
                  {isCompany && profile.founded_year && (
                    <div className="flex items-start gap-3 text-sm">
                      <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">Grundat {profile.founded_year}</span>
                    </div>
                  )}
                  {isCompany && profile.employee_count && (
                    <div className="flex items-start gap-3 text-sm">
                      <Users className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">{profile.employee_count} anställda</span>
                    </div>
                  )}
                  {isCompany && profile.website && (
                    <div className="flex items-start gap-3 text-sm">
                      <Globe className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <a
                        href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {profile.website}
                      </a>
                    </div>
                  )}


                  {isCompany && Array.isArray(profile.industry) && profile.industry.length > 0 && (
                    <div className="flex items-start gap-3 text-sm">
                      <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-muted-foreground">Bransch</span>
                        <div className="flex flex-wrap gap-1.5">
                          {profile.industry.map((i: string) => (
                            <Badge key={i} variant="secondary" className="text-xs font-normal">{i}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {isCompany && productCategories.length > 0 && (
                    <div className="flex items-start gap-3 text-sm">
                      <Package className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-muted-foreground">Produktområden</span>
                        <div className="flex flex-wrap gap-1.5">
                          {productCategories.map((c) => (
                            <Badge key={c} variant="secondary" className="text-xs font-normal">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bio — only render if a bio exists. Personal profiles currently have no bio field. */}
                {hasBio && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h2 className="text-base font-semibold">Om {isCompany ? "företaget" : "mig"}</h2>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {/* bio content placeholder */}
                    </p>
                  </div>
                )}

                {/* Reviews */}
                <div>
                  <h2 className="text-base font-semibold mb-3">Recensioner</h2>
                  {reviewCount > 0 ? (
                    <>
                      <div className="rounded-lg border border-border bg-card p-4 mb-4 flex items-center gap-4">
                        <div className="text-3xl font-display font-bold text-foreground">
                          {avgRating.toFixed(1).replace(".", ",")}
                        </div>
                        <div>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${i <= Math.round(avgRating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {reviewCount} {reviewCount === 1 ? "recension" : "recensioner"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {purchaseReviews!.map((review: any) => {
                          const ap = review.author_profile;
                          const authorName = ap?.full_name ?? "Borttagen användare";
                          const reviewDate = new Date(review.created_at).toLocaleDateString("sv-SE", {
                            day: "numeric", month: "long", year: "numeric",
                          });
                          return (
                            <div key={review.id} className="rounded-lg border border-border bg-card p-4">
                              <div className="flex items-start gap-3">
                                <Avatar className="h-9 w-9 shrink-0">
                                  {ap?.avatar_url ? <AvatarImage src={ap.avatar_url} alt={authorName} /> : null}
                                  <AvatarFallback className="text-xs">{getInitials(ap?.full_name)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-foreground">{authorName}</p>
                                  <div className="flex items-center gap-0.5 mt-0.5">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                      <Star
                                        key={i}
                                        className={`h-3.5 w-3.5 ${i <= review.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
                                      />
                                    ))}
                                  </div>
                                  {review.text && (
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{review.text}</p>
                                  )}
                                  <p className="mt-2 text-xs text-muted-foreground">{reviewDate}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border bg-card/50 p-6 text-center">
                      <Star className="mx-auto h-6 w-6 text-muted-foreground/50 mb-2" />
                      <p className="text-sm font-semibold text-foreground">Inga recensioner ännu</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Recensioner visas här efter genomförda affärer på TimberNet.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column — map + contact */}
              <div className="space-y-4 md:order-last">
                <ProfileMiniMap coords={coords} />
                {provinceKommun && (
                  <p className="flex items-center gap-2 text-sm text-foreground">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {provinceKommun}
                  </p>
                )}

                <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                  <h3 className="text-sm font-semibold">Kontaktuppgifter</h3>
                  {user ? (
                    <div className="space-y-2.5 text-sm">
                      {profileEmail ? (
                        <p className="flex items-center gap-3 text-foreground">
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                          <a href={`mailto:${profileEmail}`} className="hover:text-primary truncate">
                            {profileEmail}
                          </a>
                        </p>
                      ) : (
                        <p className="flex items-center gap-3 text-muted-foreground italic text-xs">
                          <Mail className="h-4 w-4 shrink-0" />
                          Ingen e-post angiven
                        </p>
                      )}
                      {profile.phone && (
                        <p className="flex items-center gap-3 text-foreground">
                          <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                          {profile.phone}
                        </p>
                      )}
                      {isCompany && profile.website && (
                        <p className="flex items-center gap-3 text-foreground">
                          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                          <a
                            href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                            target="_blank" rel="noopener noreferrer"
                            className="hover:text-primary break-all"
                          >
                            {profile.website}
                          </a>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Logga in för att se kontaktuppgifter
                    </p>
                  )}

                  {socialBadges.length > 0 && (
                    <div className="pt-3 border-t border-border">
                      <p className="text-[11px] font-semibold uppercase text-muted-foreground tracking-wide mb-2">
                        Sociala länkar
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {socialBadges.map((l, i) => (
                          <a
                            key={i}
                            href={l.href}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md bg-secondary text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground px-2.5 py-1.5"
                          >
                            {l.icon}
                            {l.label && <span className="max-w-[120px] truncate">{l.label}</span>}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ===== Tjänster (company only) ===== */}
          {isCompany && (
            <TabsContent value="tjanster" className="mt-6">
              {services && services.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {services.map((s: any) => (
                    <Link
                      key={s.id}
                      to={`/skogstjanster/${s.id}`}
                      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-all hover:shadow-md"
                    >
                      <div className="h-2 bg-gradient-to-r from-primary to-primary/60" />
                      <div className="p-4 flex-1 flex flex-col">
                        <Badge variant="secondary" className="self-start text-xs mb-2">
                          {s.service_type}
                        </Badge>
                        <h3 className="font-semibold text-base leading-tight mb-1 group-hover:text-primary">
                          {s.title}
                        </h3>
                        {s.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">{s.description}</p>
                        )}
                        <p className="text-sm font-semibold text-heading">
                          {s.price_model === "från" ? "Från " : ""}
                          {Number(s.base_price).toLocaleString("sv-SE")} kr
                          {s.price_model === "timme" ? "/tim" : ""}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="py-12 text-center text-muted-foreground">
                  Det här företaget erbjuder inga tjänster just nu.
                </p>
              )}
            </TabsContent>
          )}

          {/* ===== Personal (company only) ===== */}
          {isCompany && (
            <TabsContent value="personal" className="mt-6">
              {contactPersons && contactPersons.length > 0 ? (
                <>
                  <h2 className="text-sm font-semibold text-muted-foreground mb-4">
                    {contactPersons.length} {contactPersons.length === 1 ? "kontaktperson" : "kontaktpersoner"}
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {contactPersons.map((p: any) => (
                      <div
                        key={p.id}
                        className="rounded-lg border border-border bg-card p-5 flex flex-col items-center text-center"
                      >
                        <Avatar className="h-20 w-20 mb-3 border-2 border-background shadow-sm">
                          {p.photo_url ? <AvatarImage src={p.photo_url} alt={p.name} /> : null}
                          <AvatarFallback className="bg-card text-primary text-lg font-semibold">
                            {getInitials(p.name)}
                          </AvatarFallback>
                        </Avatar>
                        <p className="font-semibold text-foreground">{p.name}</p>
                        {p.role && (
                          <p className="text-xs text-muted-foreground mt-0.5">{p.role}</p>
                        )}
                        <div className="mt-3 space-y-1.5 text-xs w-full">
                          {p.phone && (
                            <p className="flex items-center justify-center gap-2 text-foreground">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground" /> {p.phone}
                            </p>
                          )}
                          {user && p.email && (
                            <p className="flex items-center justify-center gap-2 text-foreground">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              <a href={`mailto:${p.email}`} className="hover:text-primary truncate">
                                {p.email}
                              </a>
                            </p>
                          )}
                          {!user && (
                            <p className="text-muted-foreground italic">
                              Logga in för att se kontaktuppgifter
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 w-full border-primary text-primary hover:bg-primary/5"
                          onClick={() =>
                            requireAuth(() =>
                              navigate(`/meddelanden/${buildDirectThreadId(userId!, user!.id)}`)
                            )
                          }
                        >
                          <MessageSquare className="mr-2 h-3.5 w-3.5" /> Skicka meddelande
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="py-12 text-center text-muted-foreground">
                  Det här företaget har inte lagt till några kontaktpersoner.
                </p>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}
